import React, { useState } from 'react'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Theme,
    makeStyles,
} from '@material-ui/core'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import EditOutlined from '@material-ui/icons/EditOutlined'

import { EzosLoader } from '../../../../components'
import { useLeaseTerms } from '../../../../hooks/useLeaseTerms'
import {
    ApartmentLeaseTerm,
    LeaseTermInput,
} from '../../../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: theme.spacing(1),
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    title: {
        fontWeight: 700,
        fontSize: '20px',
        color: '#2F2E41',
    },
    tableWrap: {
        overflowX: 'auto',
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        '& th, & td': {
            textAlign: 'left',
            padding: theme.spacing(1, 1.5),
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            fontSize: '14px',
            whiteSpace: 'nowrap',
        },
        '& th': {
            fontWeight: 600,
            color: '#2F2E41',
        },
        '& tr:last-child td': {
            borderBottom: 'none',
        },
    },
    empty: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: '14px',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
    },
    editButton: {
        padding: 4,
    },
    form: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(2),
        minWidth: 420,
    },
    nameField: {
        gridColumn: '1 / -1',
    },
}))

const EMPTY_DRAFT: LeaseTermInput = {
    name: '',
    start_date: '',
    end_date: '',
    move_in_date: '',
    move_out_date: '',
}

function formatTermDate(iso: string): string {
    const [year, month, day] = iso.split('-').map(Number)
    if (!year || !month || !day) return iso
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

function sourceLabel(source: ApartmentLeaseTerm['source']): string {
    return source === 'from_leases' ? 'From leases' : 'Custom'
}

type Props = {
    apartmentId: number
}

export const LeaseTermsSection: React.FC<Props> = ({ apartmentId }) => {
    const classes = useStyles()
    const {
        terms,
        isLoading,
        createTerm,
        updateTerm,
        deleteTerm,
        isSaving,
    } = useLeaseTerms(apartmentId)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [open, setOpen] = useState(false)
    const [draft, setDraft] = useState<LeaseTermInput>(EMPTY_DRAFT)
    const [
        pendingDelete,
        setPendingDelete,
    ] = useState<ApartmentLeaseTerm | null>(null)

    const openCreate = () => {
        setEditingId(null)
        setDraft(EMPTY_DRAFT)
        setOpen(true)
    }

    const openEdit = (term: ApartmentLeaseTerm) => {
        setEditingId(term.id)
        setDraft({
            name: term.name,
            start_date: term.start_date,
            end_date: term.end_date,
            move_in_date: term.move_in_date,
            move_out_date: term.move_out_date,
        })
        setOpen(true)
    }

    const setField = (key: keyof LeaseTermInput, value: string) => {
        setDraft((prev) => ({ ...prev, [key]: value }))
    }

    const handleDelete = async () => {
        if (pendingDelete == null) return
        try {
            await deleteTerm(pendingDelete.id)
            setPendingDelete(null)
        } catch {
            // The hook already reports the error.
        }
    }

    const handleSave = async () => {
        try {
            if (editingId == null) {
                await createTerm(draft)
            } else {
                await updateTerm({ termId: editingId, input: draft })
            }
            setOpen(false)
        } catch {
            // The hook already reports the error.
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <span className={classes.title}>Lease Terms</span>
                <Button
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={openCreate}
                    style={{ textTransform: 'none' }}
                >
                    Add lease term
                </Button>
            </div>
            {isLoading ? (
                <EzosLoader />
            ) : !terms?.length ? (
                <div className={classes.tableWrap}>
                    <div className={classes.empty}>No lease terms yet.</div>
                </div>
            ) : (
                <div className={classes.tableWrap}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Move-in</th>
                                <th>Move-out</th>
                                <th>Source</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {terms.map((term) => (
                                <tr key={term.id}>
                                    <td>{term.name}</td>
                                    <td>{formatTermDate(term.start_date)}</td>
                                    <td>{formatTermDate(term.end_date)}</td>
                                    <td>{formatTermDate(term.move_in_date)}</td>
                                    <td>
                                        {formatTermDate(term.move_out_date)}
                                    </td>
                                    <td>{sourceLabel(term.source)}</td>
                                    <td>
                                        <div className={classes.actions}>
                                            <IconButton
                                                className={classes.editButton}
                                                aria-label={`Edit ${term.name}`}
                                                onClick={() => openEdit(term)}
                                            >
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                className={classes.editButton}
                                                aria-label={`Delete ${term.name}`}
                                                onClick={() =>
                                                    setPendingDelete(term)
                                                }
                                            >
                                                <DeleteOutline fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Dialog
                open={pendingDelete != null}
                onClose={() => setPendingDelete(null)}
                maxWidth="xs"
            >
                <DialogTitle>Delete lease term</DialogTitle>
                <DialogContent>Delete {pendingDelete?.name}?</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPendingDelete(null)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={isSaving}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm">
                <DialogTitle>
                    {editingId == null ? 'Add lease term' : 'Edit lease term'}
                </DialogTitle>
                <DialogContent>
                    <div className={classes.form}>
                        <TextField
                            className={classes.nameField}
                            label="Name"
                            value={draft.name}
                            onChange={(event) =>
                                setField('name', event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            margin="dense"
                        />
                        <TextField
                            label="Start"
                            type="date"
                            value={draft.start_date}
                            onChange={(event) =>
                                setField('start_date', event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="End"
                            type="date"
                            value={draft.end_date}
                            onChange={(event) =>
                                setField('end_date', event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Move-in"
                            type="date"
                            value={draft.move_in_date}
                            onChange={(event) =>
                                setField('move_in_date', event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Move-out"
                            type="date"
                            value={draft.move_out_date}
                            onChange={(event) =>
                                setField('move_out_date', event.target.value)
                            }
                            variant="outlined"
                            size="small"
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleSave}
                        disabled={
                            isSaving ||
                            !draft.name.trim() ||
                            !draft.start_date ||
                            !draft.end_date ||
                            !draft.move_in_date ||
                            !draft.move_out_date
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
